import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { X, CheckCircle, Storefront, ArrowRight, Confetti } from "@phosphor-icons/react";
import api from "../lib/api";

/**
 * ProfileOnboardingTour — one-time guided walkthrough fired on the
 * owner dashboard when:
 *   1. the owner has a published profile, AND
 *   2. it isn't yet `is_complete` (score < threshold), AND
 *   3. they haven't already dismissed the tour
 *
 * Uses the SAME completeness signals as the checklist widget so the
 * "next thing to do" can never disagree between the two surfaces.
 *
 * Carousel walks through each missing signal one at a time. "Add this"
 * routes to the profile editor; "Skip" advances; "Maybe later" dismisses
 * the whole tour (POST /api/owner/onboarding/dismiss) so it never
 * re-opens.
 *
 * Designed to be friendly, not pushy — owners can dismiss at any time
 * and the next missing-signal nudge is the lighter checklist card.
 */
export default function ProfileOnboardingTour({ data, onDismiss }) {
  const navigate = useNavigate();
  const [stepIdx, setStepIdx] = useState(0);
  const [open, setOpen] = useState(true);
  const [dismissing, setDismissing] = useState(false);

  const missing = (data?.signals || []).filter((s) => !s.satisfied);
  const total = data?.total || 7;
  const score = data?.score || 0;

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => { if (e.key === "Escape") handleDismiss(); };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }); // intentional: re-bind on every render so handleDismiss is fresh

  const handleDismiss = async () => {
    setDismissing(true);
    try {
      await api.post("/owner/onboarding/dismiss", {});
    } catch {
      // Silent — worst case we re-show the tour next load and they dismiss again.
    }
    setOpen(false);
    onDismiss?.();
  };

  const handleAddThis = (signalKey) => {
    // Route to the editor with a hash so we can scroll to the right
    // section. Editor already supports the hash; this is just a
    // best-effort UX nicety.
    navigate(`/dashboard/profile-editor#${signalKey}`);
    // Fire-and-forget dismiss — once they're in the editor we don't
    // want this to re-open even if they bounce back.
    api.post("/owner/onboarding/dismiss", {}).catch(() => {});
  };

  if (!open || missing.length === 0) return null;

  const step = missing[Math.min(stepIdx, missing.length - 1)];
  const isLast = stepIdx >= missing.length - 1;

  return (
    <div
      className="fixed inset-0 z-[100] grid place-items-center px-4 py-8 bg-black/60 backdrop-blur-sm animate-in fade-in"
      role="dialog"
      aria-modal="true"
      aria-labelledby="onboarding-tour-title"
      data-testid="profile-onboarding-tour"
      onClick={handleDismiss}
    >
      <div
        className="relative w-full max-w-md rounded-2xl bg-card border border-border shadow-2xl overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          type="button"
          onClick={handleDismiss}
          disabled={dismissing}
          className="absolute top-3 right-3 h-9 w-9 rounded-full grid place-items-center text-muted-foreground hover:bg-muted transition"
          aria-label="Close tour"
          data-testid="onboarding-tour-close"
        >
          <X size={18} weight="bold" />
        </button>

        {/* Header */}
        <div className="px-6 pt-6 pb-3 bg-gradient-to-br from-primary/10 via-card to-card">
          <div className="inline-flex items-center gap-1.5 h-6 px-2.5 rounded-full bg-primary/15 text-primary text-[10px] font-bold uppercase tracking-wider mb-2">
            <Confetti weight="fill" size={10} /> Welcome to TruckTrackr
          </div>
          <h2
            id="onboarding-tour-title"
            className="font-display font-bold text-xl leading-tight"
          >
            Let's finish your profile in 60 seconds.
          </h2>
          <p className="text-xs text-muted-foreground mt-1.5">
            Owners with complete profiles get <strong>more discovery, more clicks,
            and higher placement</strong> in our public directory.
          </p>
        </div>

        {/* Progress dots */}
        <div className="px-6 pt-4">
          <div className="flex items-center gap-1.5" aria-hidden>
            {missing.map((_, i) => (
              <span
                key={i}
                className={`h-1.5 flex-1 rounded-full transition ${
                  i < stepIdx
                    ? "bg-primary"
                    : i === stepIdx
                    ? "bg-primary"
                    : "bg-muted"
                }`}
              />
            ))}
          </div>
          <p className="text-[11px] text-muted-foreground mt-2 tabular-nums">
            Step {stepIdx + 1} of {missing.length} · {score}/{total} complete
          </p>
        </div>

        {/* Current step */}
        <div className="px-6 py-5">
          <div className="flex items-start gap-3">
            <div className="h-10 w-10 rounded-xl bg-primary/15 grid place-items-center flex-none">
              <Storefront weight="duotone" size={20} className="text-primary" />
            </div>
            <div className="min-w-0">
              <div className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
                Next up
              </div>
              <h3 className="font-display font-bold text-lg leading-snug mt-0.5">
                {step.label}
              </h3>
              <p className="text-xs text-muted-foreground mt-1">
                {STEP_HINTS[step.key] || "Tap below to jump into the editor."}
              </p>
            </div>
          </div>
        </div>

        {/* Actions */}
        <div className="px-6 pb-5 flex items-center gap-2">
          <button
            type="button"
            onClick={handleDismiss}
            disabled={dismissing}
            className="text-xs font-semibold text-muted-foreground hover:text-foreground"
            data-testid="onboarding-tour-skip-all"
          >
            Maybe later
          </button>
          <div className="flex-1" />
          {!isLast && (
            <button
              type="button"
              onClick={() => setStepIdx((i) => i + 1)}
              className="h-9 px-3.5 rounded-full border border-border text-xs font-semibold hover:bg-muted transition-colors"
              data-testid="onboarding-tour-skip-step"
            >
              Skip
            </button>
          )}
          <button
            type="button"
            onClick={() => handleAddThis(step.key)}
            className="inline-flex items-center gap-1.5 h-9 px-4 rounded-full bg-primary text-primary-foreground text-xs font-semibold hover:bg-primary/90 transition-colors"
            data-testid="onboarding-tour-add-step"
          >
            Add this <ArrowRight size={12} weight="bold" />
          </button>
        </div>

        {/* Already-done preview */}
        {score > 0 && (
          <div className="px-6 pb-5 pt-3 border-t border-border">
            <div className="text-[10px] font-bold uppercase tracking-wider text-emerald-600 dark:text-emerald-400 mb-1.5">
              Already done
            </div>
            <ul className="flex flex-wrap gap-1.5">
              {(data?.signals || [])
                .filter((s) => s.satisfied)
                .map((s) => (
                  <li
                    key={s.key}
                    className="inline-flex items-center gap-1 text-[11px] text-muted-foreground bg-muted px-2 py-0.5 rounded-full"
                  >
                    <CheckCircle weight="fill" size={11} className="text-emerald-500" />
                    {s.label.replace(/^(Upload|Add|Write|Link or upload|Set) (a |an |at least one |your |a social or contact )?/i, "")}
                  </li>
                ))}
            </ul>
          </div>
        )}
      </div>
    </div>
  );
}

// Short, encouraging hints per signal — surfaced in the tour and nowhere
// else so we don't bloat the checklist card.
const STEP_HINTS = {
  logo: "A square logo makes your profile instantly recognizable — and it's the first thing customers see in the directory.",
  cover: "A wide cover photo turns your profile into a real landing page. Pick one that shows the truck or your best dish.",
  description: "Two or three lines: what you serve, where you go, what's special about you.",
  menu: "Drop your menu URL (link to Instagram highlights, Google Doc, or upload a PDF). Customers want to know before they drive over.",
  gallery: "One or two food photos go a long way — the lightbox makes them shine.",
  social: "Add a website, Instagram, Facebook, or TikTok so customers can keep up with you between visits.",
};
